import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Tabs,
  Tab,
  Form,
  Alert,
  Badge,
} from "react-bootstrap";
import { FaSave } from "react-icons/fa";
import {
  getHistoryNotes,
  createAccountNotes,
  updateAccountNotes,
  updateAccountTag,
} from "../../api";
import { jwtDecode } from "jwt-decode";
import "./AccountNotes.css";

// Shared palette — must stay in sync with the ALLOWED_COLORS list in
// backend/src/accountRoutes.js POST /account/tag.
export const TAG_COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#f59e0b", // amber
  "#16a34a", // green
  "#14b8a6", // teal
  "#3b82f6", // blue
  "#a855f7", // purple
  "#ec4899", // pink
];
const DEFAULT_TAG_COLOR = TAG_COLORS[3]; // green

// NotesHistoryModal now uses onHide prop for closing
const NotesHistoryModal = ({ show, onHide, account, onTagChange }) => {
  const accountId = account?._id;
  const [activeTab, setActiveTab] = useState("notes");
  const [user, setUser] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [tagText, setTagText] = useState("");
  const [tagColor, setTagColor] = useState(DEFAULT_TAG_COLOR);
  const [tagSaving, setTagSaving] = useState(false);
  const [hasSavedTag, setHasSavedTag] = useState(false);
  // The note currently open for editing, and its working text. Only one note
  // is editable at a time — a rail of simultaneously-open textareas invites
  // losing whichever one you didn't save.
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Load notes and user when modal opens
  useEffect(() => {
    if (show && accountId) {
      loadNotes();
      const token = sessionStorage.getItem("user");
      if (token) {
        const decoded = jwtDecode(token);
        setUser(decoded);
      }
    }
  }, [show, accountId]);

  // Seed tag fields from the account doc each time the modal opens or the
  // parent updates the account (e.g. after a successful save).
  useEffect(() => {
    if (!show) return;
    setTagText(account?.accountTagText || "");
    setTagColor(account?.accountTagColor || DEFAULT_TAG_COLOR);
    setHasSavedTag(!!account?.accountTagText);
  }, [show, account?.accountTagText, account?.accountTagColor]);

  const handleSaveTag = async () => {
    const trimmed = tagText.trim();
    if (!trimmed) return;
    setTagSaving(true);
    try {
      const result = await updateAccountTag(accountId, trimmed, tagColor);
      setHasSavedTag(true);
      onTagChange?.(result.accountTagText, result.accountTagColor);
    } catch (err) {
      console.error(err);
      setError("Could not save tag.");
    } finally {
      setTagSaving(false);
    }
  };

  const handleClearTag = async () => {
    setTagSaving(true);
    try {
      const result = await updateAccountTag(accountId, "", null);
      setTagText("");
      setHasSavedTag(false);
      onTagChange?.(result.accountTagText, result.accountTagColor);
    } catch (err) {
      console.error(err);
      setError("Could not clear tag.");
    } finally {
      setTagSaving(false);
    }
  };

  const loadNotes = async () => {
    try {
      const data = await getHistoryNotes(accountId);
      // Separate into notes and history based on type
      const notesArray = data
        .filter((item) => item.type === "Notes")
        .map((item) => ({ ...item, id: item._id || item.id }))
        .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
      const historyArray = data
        .filter((item) => item.type === "History")
        .map((item) => ({ ...item, id: item._id || item.id }))
        .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

      setNotes(notesArray);
      setHistory(historyArray);
    } catch (err) {
      console.error(err);
      setError("Failed to load notes and history.");
    }
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    const userName = user
      ? `${user.firstName} ${user.lastName}`
      : "Unknown User";
    const payload = {
      accountId,
      type: "Notes",
      note: text,
      // A new note starts off the statement; the button on its row puts it on.
      // Author and dateCreated are set server-side from the token and clock —
      // sending them here was ignored at best.
      displayOnStatement: false,
    };
    try {
      const created = await createAccountNotes(payload);
      // The create endpoint answers { message, insertedId } — it does not echo
      // the document back. Reading _id / id off that response put a row in the
      // list with NO id and no userId, so the statement button posted to
      // /update/undefined (a 400) and the edit button never appeared. Both
      // recovered on reopening the modal, because that refetches from the
      // server — which is exactly what made it look intermittent.
      const newId = created?.insertedId || created?._id || created?.id;
      if (!newId) {
        // Saved, but we can't address it. Refetch rather than seed a row that
        // every action on it would fail against.
        await loadNotes();
        setNoteText("");
        return;
      }
      setNotes((prev) => [
        {
          _id: newId,
          id: String(newId),
          type: "Notes",
          note: text,
          displayOnStatement: false,
          // Authorship decides who may edit. The server takes it from the
          // token, which is this user — so the note is immediately editable
          // without waiting for a refetch.
          userId: user?._id || null,
          userName,
          dateCreated: new Date().toISOString(),
        },
        ...prev,
      ]);
      setNoteText("");
    } catch (err) {
      console.error(err);
      setError("Could not add note.");
    }
  };

  /** A note is editable by the person who wrote it, and nobody else. */
  const canEdit = (note) =>
    !!user?._id && String(note.userId || "") === String(user._id);

  const startEdit = (note) => {
    setError(null);
    setEditingId(note.id);
    setEditText(note.note || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (note) => {
    const text = editText.trim();
    if (!text) return;
    // Nothing changed — close the editor rather than spend a request saying so.
    if (text === (note.note || "").trim()) {
      cancelEdit();
      return;
    }
    setEditSaving(true);
    try {
      await updateAccountNotes(note._id || note.id, { note: text });
      const editedAt = new Date().toISOString();
      setNotes((prev) =>
        prev.map((n) =>
          n.id === note.id ? { ...n, note: text, dateUpdated: editedAt } : n,
        ),
      );
      cancelEdit();
    } catch (err) {
      console.error(err);
      // The server refuses an edit to someone else's note. The button is only
      // rendered on your own, so this is the stale-page case: say which it is
      // rather than "could not save".
      setError(
        err?.response?.status === 403
          ? "You can only edit your own notes."
          : "Could not save the note.",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleDisplayFlag = async (note) => {
    try {
      // Use _id for the API call (MongoDB ID)
      const noteId = note._id || note.id;
      
      // Update in database
      await updateAccountNotes(noteId, { displayOnStatement: !note.displayOnStatement });
      
      // Update local state
      setNotes((prev) =>
        prev.map((n) =>
          n.id === note.id
            ? { ...n, displayOnStatement: !note.displayOnStatement }
            : n
        )
      );
    } catch (err) {
      console.error(err);
      setError("Could not update display flag.");
    }
  };

  const formatDate = (isoString) => {
    const d = new Date(isoString);
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <Modal
      show={show}
      size="lg"
      aria-labelledby="notes-history-modal"
      centered
      scrollable
      onHide={onHide}
    >
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" id="notes-history-modal" className="m-0">
          Account Notes
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="account-notes-modal">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            id="notes-history-tabs"
            className="account-notes-tabs px-3 pt-2"
          >
            <Tab
              eventKey="tag"
              title={
                <span>
                  <i className="fa-solid fa-tag me-2" />
                  Tag
                </span>
              }
            />
            <Tab
              eventKey="notes"
              title={
                <span>
                  <i className="fa-regular fa-note-sticky me-2" />
                  Notes
                </span>
              }
            />
            <Tab
              eventKey="history"
              title={
                <span>
                  <i className="fa-solid fa-clock-rotate-left me-2" />
                  History
                </span>
              }
            />
          </Tabs>

          <div className="px-4 py-3">
            {/* ─── TAG TAB ─────────────────────────────────────────────── */}
            {activeTab === "tag" && (
              <>
                <p className="text-muted small mb-3">
                  Add a short coloured label for this account. The tag and
                  colour appear next to the EDI badge on the account header
                  and as a coloured strip on the account info card.
                </p>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-end mb-1">
                    <Form.Label className="fw-bold mb-0">Tag text</Form.Label>
                    <small className="text-muted">{tagText.length}/50</small>
                  </div>
                  <Form.Control
                    type="text"
                    maxLength={50}
                    value={tagText}
                    onChange={(e) => setTagText(e.target.value)}
                    placeholder="Short label, e.g. 'VIP - call before invoice'"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold mb-2">Colour</Form.Label>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {TAG_COLORS.map((c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => setTagColor(c)}
                        aria-label={`Choose color ${c}`}
                        title={c}
                        className={`tag-swatch${tagColor === c ? " selected" : ""}`}
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                </Form.Group>

                {tagText.trim() && (
                  <div className="mb-3">
                    <small className="text-muted me-2">Preview:</small>
                    <span
                      className="badge"
                      style={{ backgroundColor: tagColor, color: "#fff" }}
                    >
                      {tagText.trim()}
                    </span>
                  </div>
                )}

                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSaveTag}
                    disabled={tagSaving || !tagText.trim()}
                  >
                    <FaSave className="me-1" /> Save Tag
                  </Button>
                  {hasSavedTag && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleClearTag}
                      disabled={tagSaving}
                    >
                      Clear Tag
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* ─── NOTES TAB ───────────────────────────────────────────── */}
            {activeTab === "notes" && (
              <>
                <Form.Group controlId="noteText" className="mb-2">
                  <Form.Label className="fw-bold">Add Note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Type your note here..."
                  />
                </Form.Group>
                <Button
                  variant="primary"
                  onClick={handleAddNote}
                  className="mb-4"
                  disabled={!noteText.trim()}
                >
                  <FaSave className="me-1" /> Save Note
                </Button>

                <Form.Label className="fw-bold d-block mb-2">
                  Previous Notes
                </Form.Label>
                {notes.length === 0 ? (
                  <div className="note-list-empty">No notes yet.</div>
                ) : (
                  notes.map((item) => (
                    <div key={item.id} className="note-row">
                      {editingId === item.id ? (
                        <>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            disabled={editSaving}
                            autoFocus
                            className="mb-2"
                          />
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSaveEdit(item)}
                              disabled={editSaving || !editText.trim()}
                            >
                              <FaSave className="me-1" /> Save
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={cancelEdit}
                              disabled={editSaving}
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <p className="mb-0 fw-semibold flex-grow-1 me-2">
                              {item.note}
                            </p>
                            <div className="d-flex align-items-center gap-2">
                              {item.displayOnStatement && (
                                <Badge bg="info">On Statement</Badge>
                              )}
                              {canEdit(item) && (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => startEdit(item)}
                                  title="Edit note"
                                >
                                  <i className="fa-solid fa-pen" />
                                </Button>
                              )}
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleToggleDisplayFlag(item)}
                                title={
                                  item.displayOnStatement
                                    ? "Remove from statement"
                                    : "Add to statement"
                                }
                              >
                                <i
                                  className={`fa-solid fa-${
                                    item.displayOnStatement ? "minus" : "plus"
                                  } me-1`}
                                  style={{ fontSize: "0.75rem" }}
                                />
                                <i className="fa-solid fa-file-lines" />
                              </Button>
                            </div>
                          </div>
                          <div className="d-flex justify-content-end note-meta">
                            {item.userName} • {formatDate(item.dateCreated)}
                            {/* A note can go out on a patient statement, so an
                                edited one says so rather than quietly replacing
                                what was there. */}
                            {item.dateUpdated && (
                              <span className="ms-1">
                                (edited {formatDate(item.dateUpdated)})
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </>
            )}

            {/* ─── HISTORY TAB ─────────────────────────────────────────── */}
            {activeTab === "history" && (
              <>
                <Form.Label className="fw-bold d-block mb-2">
                  History Records
                </Form.Label>
                {history.length === 0 ? (
                  <div className="note-list-empty">No history records yet.</div>
                ) : (
                  history.map((item) => (
                    <div key={item.id || item._id} className="note-row">
                      <div className="d-flex justify-content-between align-items-start">
                        <p className="mb-1 flex-grow-1 me-2">
                          {item.actionDescription}
                        </p>
                        <small className="note-meta text-nowrap">
                          {item.userName} •{" "}
                          {new Date(item.dateCreated).toLocaleString()}
                        </small>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export function NotesHistory({ account, show, onHide, onTagChange }) {
  // Controlled mode: parent supplies show + onHide → skip the internal trigger
  // button. Uncontrolled mode preserves the legacy Patient-screen behaviour.
  const isControlled = show !== undefined && typeof onHide === "function";
  const [showModal, setShowModal] = useState(false);

  const open = isControlled ? show : showModal;
  const close = isControlled ? onHide : () => setShowModal(false);

  return (
    <>
      {!isControlled && (
        <Button
          variant="primary"
          className="w-50 d-flex align-items-center justify-content-center gap-3"
          onClick={() => setShowModal(true)}
        >
          <i className="fa-regular fa-pen-to-square" /> Account Notes
        </Button>
      )}

      <NotesHistoryModal
        show={open}
        onHide={close}
        account={account}
        onTagChange={onTagChange}
      />
    </>
  );
}